import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { ImageField } from "./ImageField";
import { api, apiError, mediaUrl } from "../../lib/api";

const EMPTY = { title: "", sector: "", location: "", period: "", summary: "", challenge: "", solution: "", results: [], image: "", published: false, order: 0 };
const inputCls = "w-full rounded-lg bg-white/[0.04] border hairline px-3 py-2 text-sm text-white outline-none focus:border-[#4fc3f7]/60";

const F = ({ label, children }) => <label className="block"><span className="block text-xs text-slate-400 mb-1.5">{label}</span>{children}</label>;

const CaseForm = ({ value, onSaved, onClose }) => {
  const [f, setF] = useState(value);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const resultsText = (f.results || []).map((r) => `${r.value} | ${r.label}`).join("\n");
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, order: Number(f.order) || 0 };
      delete body.id; delete body.created_at; delete body.updated_at;
      if (value.id) await api.put(`/admin/case-studies/${value.id}`, body);
      else await api.post("/admin/case-studies", body);
      toast.success("Caso studio salvato");
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="case-form">
      <F label="Titolo"><input data-testid="case-title" className={inputCls} value={f.title} onChange={set("title")} /></F>
      <div className="grid sm:grid-cols-3 gap-3">
        <F label="Settore"><input data-testid="case-sector" className={inputCls} value={f.sector} onChange={set("sector")} /></F>
        <F label="Zona"><input className={inputCls} value={f.location} onChange={set("location")} /></F>
        <F label="Periodo"><input className={inputCls} value={f.period} onChange={set("period")} /></F>
      </div>
      <F label="Sintesi"><textarea rows={2} className={inputCls} value={f.summary} onChange={set("summary")} /></F>
      <F label="Situazione di partenza"><textarea rows={3} className={inputCls} value={f.challenge} onChange={set("challenge")} /></F>
      <F label="Cosa abbiamo fatto"><textarea rows={3} className={inputCls} value={f.solution} onChange={set("solution")} /></F>
      <F label="Risultati verificati (uno per riga: valore | descrizione)">
        <textarea data-testid="case-results" rows={3} className={inputCls} defaultValue={resultsText}
          onChange={(e) => setF((x) => ({ ...x, results: e.target.value.split("\n").filter(Boolean).map((l) => { const [v, ...rest] = l.split("|"); return { value: v.trim(), label: rest.join("|").trim() }; }) }))} />
      </F>
      <F label="Immagine"><ImageField value={f.image} onChange={(v) => setF((x) => ({ ...x, image: v }))} testId="case-image" /></F>
      <div className="flex items-center justify-between gap-4">
        <label className="flex items-center gap-3 text-sm text-white"><Switch data-testid="case-published" checked={f.published} onCheckedChange={(v) => setF((x) => ({ ...x, published: v }))} />Pubblicato</label>
        <F label="Ordine"><input type="number" className={`${inputCls} w-24`} value={f.order} onChange={set("order")} /></F>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className="h-10 px-4 rounded-full border hairline text-sm text-white">Annulla</button>
        <button data-testid="case-save" onClick={save} disabled={busy || f.title.length < 2} className="h-10 px-5 rounded-full bg-[#ff5500] text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}Salva
        </button>
      </div>
    </div>
  );
};

export const CasesTab = () => {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/case-studies").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Eliminare questo caso studio?")) return;
    await api.delete(`/admin/case-studies/${id}`);
    load();
  };
  return (
    <div data-testid="cases-tab">
      <div className="flex items-center justify-between mb-5 gap-4">
        <p className="text-sm text-slate-400">La sezione Casi studio compare sul sito solo quando almeno un caso è pubblicato. Inserisci solo risultati verificabili.</p>
        <button data-testid="case-new" onClick={() => setEdit(EMPTY)} className="shrink-0 h-10 px-4 rounded-full bg-[#ff5500] text-white text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" />Nuovo</button>
      </div>
      {items.length === 0 && <p className="text-slate-400 text-sm py-10 text-center" data-testid="cases-empty">Nessun caso studio.</p>}
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((c) => (
          <div key={c.id} data-testid={`admin-case-${c.id}`} className="rounded-2xl border hairline bg-white/[0.02] p-4 flex gap-4">
            <div className="w-20 h-20 rounded-xl overflow-hidden bg-white/5 shrink-0">{c.image && <img src={mediaUrl(c.image)} alt="" className="w-full h-full object-cover" />}</div>
            <div className="min-w-0 flex-1">
              <p className="text-white font-semibold truncate">{c.title}</p>
              <p className="text-xs text-slate-400">{c.sector} {c.location && `· ${c.location}`}</p>
              <p className={`mt-2 text-xs inline-flex items-center gap-1 ${c.published ? "text-emerald-300" : "text-slate-400"}`}>{c.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}{c.published ? "Pubblicato" : "Bozza"}</p>
            </div>
            <div className="flex flex-col gap-1">
              <button data-testid={`case-edit-${c.id}`} onClick={() => setEdit(c)} className="w-8 h-8 grid place-items-center rounded-lg text-slate-300 hover:text-white" aria-label="Modifica"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`case-delete-${c.id}`} onClick={() => remove(c.id)} className="w-8 h-8 grid place-items-center rounded-lg text-slate-400 hover:text-red-400" aria-label="Elimina"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0b1222] border hairline text-white">
          <DialogTitle>{edit?.id ? "Modifica caso studio" : "Nuovo caso studio"}</DialogTitle>
          {edit && <CaseForm value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
